import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from './AdminSidebar';
import NotificationBell from './NotificationBell';
import { LogOut } from 'lucide-react';

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    if (!user) return;
    supabase
      .from('admin_profiles')
      .select('display_name, first_name')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setDisplayName(data.display_name || data.first_name || user.email?.split('@')[0] || 'Admin');
        } else {
          setDisplayName(user.email?.split('@')[0] || 'Admin');
        }
      });
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut();
      window.location.href = '/admin/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const initials = displayName ? displayName[0].toUpperCase() : 'A';

  return (
    <div className="admin-portal min-h-screen bg-gray-50 flex">
      <AdminSidebar />

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <h1 className="text-xl font-semibold text-gray-900">Admin Panel</h1>

              <div className="flex items-center gap-3">
                <NotificationBell />

                <Link
                  to="/admin/account-settings"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors group"
                  title="Account Settings"
                >
                  <div className="w-8 h-8 rounded-full bg-[#40E0D0]/15 flex items-center justify-center group-hover:bg-[#40E0D0]/25 transition-colors">
                    <span className="text-sm font-bold text-[#40E0D0]">{initials}</span>
                  </div>
                  <span className="text-sm text-gray-700 font-medium hidden sm:block">{displayName}</span>
                </Link>

                <div className="w-px h-6 bg-gray-200" />

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 transition-colors px-2 py-1.5 rounded-lg hover:bg-gray-100"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="text-sm hidden sm:block">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
